import { OverlayPortal as FloatingPortal } from "@/components/modal/OverlayPortal";
// ------------------------------------------------------------------
// DropdownImplV2 — private shared behaviour behind ComboboxV2 and SelectMenuV2.
//
// Not for direct use: import ComboboxV2 when the list wants search, or
// SelectMenuV2 when it does not. Those two are the public API, and each pins
// `mode` itself, which is what makes a "select that also filters" or a
// "select with freeform custom values" unrepresentable rather than merely
// discouraged.
//
// A select is a combobox whose query is always empty, so the hard parts —
// keyboard navigation, listbox semantics, floating placement, grouping,
// virtualization, dark theming — are written once, here.
// ------------------------------------------------------------------

import { Icon, IconName, IconSize } from "@/components/icons/Icon";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { IconButtonV2 } from "@/components/interactive/ButtonV2";
import { CheckboxV2Control } from "@/components/interactive/CheckboxV2";
import {
  type DropdownEntry,
  type DropdownOption,
  type DropdownOptionState,
  type DropdownV2Size,
} from "@/components/interactive/DropdownV2.types";
import { FieldLabelV2 } from "@/components/interactive/FieldLabelV2";
import { LoadingSpinnerWithTextV2 } from "@/components/interactive/LoadingSpinnerV2";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { MenuItemV2 } from "@/components/interactive/MenuItemV2";
import {
  MenuScrollEndFade,
  type MenuScrollEndFadeHandle,
} from "@/components/interactive/MenuScrollEndFade";
import { VirtualizedList } from "@/components/interactive/VirtualizedList";
import {
  announceDropdownMenuOpen,
  subscribeToOtherDropdownMenuOpen,
} from "@/components/interactive/dropdownMenuCoordination";
import { fullRowsMenuMaxHeight } from "@/components/interactive/dropdown-menu-height";
import {
  filterDropdownEntries,
  findDropdownOption,
  groupDropdownSections,
  hasExactDropdownOptionMatch,
  normalizeDropdownQuery,
} from "@/components/interactive/dropdown-option-utils";
import { useDropdownKeyboardHandlers } from "@/components/interactive/use-dropdown-keyboard-handlers";
import {
  MENU_TRIGGER_GAP,
  MENU_VIEWPORT_MARGIN,
  useDropdownFloatingV2,
} from "@/components/interactive/use-dropdown-floating-v2";
import { Skeleton } from "@/components/loaders/Skeleton";
import { DesignSystemTypography, TextVariant } from "@/components/typography/design-system";
import { ZIndex } from "@/constants/z-index";
import { AmplitudeEvent, useAmplitudeOnClick } from "@/utils/amplitude";
import { classNames } from "@/utils/classnames";
import { useEventCallback } from "@/utils/hooks/use-event-callback";
import mergeRefs from "@/utils/merge-refs";
import { staticEstimateSVGTextWidth } from "@/utils/string-utils";
import { type Placement } from "@floating-ui/react";
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export {
  DropdownV2Sizes,
  type DropdownEntry,
  type DropdownOption,
  type DropdownOptionAccessory,
  type DropdownOptionGroup,
  type DropdownOptionState,
  type DropdownV2Size,
} from "@/components/interactive/DropdownV2.types";

// Tallest the open menu is allowed to get before it is trimmed to a whole number
// of rows; the runtime sizing shrinks it further when there's less room below
// the input (e.g. near a modal's bottom).
const MENU_MAX_HEIGHT_BUDGET = 300;
// The chrome between the menu's edge and its rows: MenuContainerV2's border,
// MenuSectionV2's inset, and the in-menu search row's divider (3px, a 1px
// stroke, 3px).
const MENU_BORDER_WIDTH = 1;
const MENU_SECTION_INSET = 3;
const MENU_SEARCH_DIVIDER_HEIGHT = 7;
// A thin scrollbar that stays visible, so a long list shows that it scrolls
// even where the OS hides scrollbars at rest, with no reserved gutter, so a
// list that fits keeps no empty strip.
const MENU_SCROLLER_CLASS = "overflow-y-auto visible-scrollbar no-scrollbar-gutter";
const SCROLL_BOTTOM_THRESHOLD_PX = 16;
/**
 * Which of the two public components is being rendered.
 *
 * A discriminated union rather than a `searchable` boolean so that props which
 * only make sense with a query — freeform custom values, and their length cap —
 * are unreachable in select mode. There is no way to express
 * `searchable={false} allowCustomValue`, because a select has nothing to type
 * a custom value into.
 */
export type DropdownV2Mode =
  | {
      kind: "combobox";
      allowCustomValue: boolean;
      customValueMaxLength: number | null;
      /** Single combobox only: leaving the field commits what was typed. */
      commitCustomValueOnBlur: boolean;
    }
  | { kind: "select"; includeNoneOption: boolean }
  // A select whose query lives *inside the menu*: the trigger stays a button (so
  // `valueRenderer` works — a value can be a node, an input's value cannot), and
  // a search row at the top of the open menu drives the same filtering a
  // combobox has. Like "select", the arm carries no custom-value fields, so a
  // freeform option is unrepresentable: there is a query, but committing it as a
  // value is not what this control means.
  | { kind: "searchable-select"; includeNoneOption: boolean };

// `includeNoneOption` on either non-combobox arm renders a "None" row ahead of
// the options that commits null — the clear affordance neither has a trigger
// button for, in place of combobox's dedicated clear icon. Single-select only:
// a multi trigger already clears one value at a time by removing its chip.

/** Omit that distributes over a union, so variant-specific props survive it. */
export type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;

/**
 * Props that both public dropdowns — `ComboboxV2` and `SelectMenuV2` — expose
 * identically.
 *
 * Declared once and folded into both surfaces, so a prop added here reaches both
 * rather than being wired up for one and forgotten for the other. That is exactly
 * how `SelectMenuV2` ended up without `itemRenderer` while the shared
 * implementation had supported it all along.
 *
 * Props that genuinely differ stay out: `options` (each has its own option
 * shape), `label` (required on the impl, optional on `SelectMenuV2`), and
 * anything that only makes sense with a query, such as `filterEntries`.
 */
// The muted None row's ink, the same as a branch menu's "No branch" row. The row's own label
// color is set on the row, so the span overrides it with `!`.
const MUTED_NONE_OPTION_TEXT_CLASS = "!text-cds-ink-muted dark:!text-zinc-500";

export type SharedDropdownV2Props<T extends string | number> = {
  size?: DropdownV2Size;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  /**
   * The icon shown when `triggerVariant` is `"icon"` (declared per-variant on
   * `SingleDropdownImplV2Props`/`MultiDropdownImplV2Props`, since only that one
   * value of `triggerVariant` is valid on both). Required in practice whenever
   * `triggerVariant` is `"icon"`.
   */
  triggerIcon?: IconName;
  /**
   * A ref to the trigger's DOM node, for a caller that needs to open the menu
   * programmatically (e.g. `ref.current?.click()` from a keyboard shortcut).
   * Only wired up for `triggerVariant: "icon"` today - no other trigger shape
   * has a caller that needs it yet.
   */
  triggerRef?: React.Ref<HTMLButtonElement>;
  /**
   * The open menu's stack layer. A dropdown inside a surface stacked above
   * `ZIndex.POPOVER` passes that surface's layer, so the menu, mounted after it,
   * opens on top of it.
   */
  menuZIndex?: ZIndex;
  /**
   * Replace the trigger with a non-interactive skeleton while the field's options
   * are still being fetched.
   *
   * Whole-field, not per-menu: a loading field is disabled, and a disabled field
   * closes and cannot reopen its menu, so there is no state in which the menu is
   * open while this is true. A dropdown that stays usable while its options
   * refetch in the background is a different thing and would need its own flag —
   * this one means "the field is not ready yet".
   */
  loading?: boolean;
  /**
   * Renders the body of each option row in place of its label. Row chrome —
   * height, hover, the active and selected states, keyboard handling — stays with
   * the dropdown; only the content is yours. `option.label` remains the row's
   * accessible name, so keep it meaningful.
   *
   * Height staying with the dropdown means a single line: a row is 28px (`sm`) or
   * 32px (`md`), and content taller than that overflows rather than growing the
   * row. For a second line use `DropdownOption.subtitle`, which switches the row
   * to the growable two-line shape.
   */
  itemRenderer?: (option: DropdownOption<T>, state: DropdownOptionState) => React.ReactNode;
  /**
   * Renders a *selected* value — a multi chip's body, or a select-mode single
   * trigger's content — in place of the option's label. Defaults to
   * `itemRenderer`, so custom rows carry into the trigger unless the two are
   * split on purpose (menu rows often have room for detail the trigger lacks).
   *
   * The trigger and the chips are single-line and fixed-height. An `itemRenderer`
   * that is only safe at menu width therefore needs a `valueRenderer` beside it,
   * or it overflows here too.
   *
   * An explicit renderer owns the single trigger's content, including any
   * leading icon. Without one, that trigger renders the selected option's
   * `leftAccessory` before its label (or the `itemRenderer` fallback).
   *
   * Called with `{ active: false, selected: true }`: value position is not the
   * keyboard cursor and is selected by definition. Freeform custom values have no
   * option to render, so they always fall back to their raw string. Unavailable
   * on a searchable single trigger,
   * which is an editable input — its value can only ever be a string.
   */
  valueRenderer?: (option: DropdownOption<T>, state: DropdownOptionState) => React.ReactNode;
  /** Labels/accessories for selected values outside the current menu results. Never adds menu rows. */
  selectedOptions?: readonly DropdownOption<T>[];
  /**
   * Overrides the trigger's accessible name, for a field labelled by surrounding
   * copy rather than by its own `label`. Without this the label wins, because a
   * `<label for>` beats an element's text content.
   */
  triggerAriaLabel?: string;
  /** Fires when focus leaves the field entirely, after the menu has closed. */
  onBlur?: () => void;
  /** Compact action rendered inline after the field label. */
  labelTrailingAction?: ReactNode;
  /** Fires when the option scroller reaches its bottom edge. */
  onScrollBottom?: () => void;
  /** Shows an in-menu progress row while the next page of options is loading. */
  loadingMore?: boolean;
  /** Control row rendered in a separated section below the option scroller. */
  menuFooter?: ReactNode;
  /** Class for the scrollable menu surface, e.g. to cap its height. */
  menuClassName?: string;
  /**
   * `"trigger"` matches the field's width, `"content"` hugs the options, and a
   * number or CSS length pins it explicitly.
   */
  menuWidth?: "trigger" | "content" | number | string;
  /** Preferred floating-ui placement before viewport flipping and shifting are applied. */
  menuPlacement?: Placement;
  /** Shifts the menu horizontally from its trigger anchor. */
  className?: string;
  /** Class for the bordered trigger container inside the field root. */
  containerClassName?: string;
  /**
   * Keep every option row indented by a leading-control slot, even rows with no
   * `leftIcon` or `leftAccessory`, so all labels start on one line.
   *
   * Off by default, which is right for the common menu where no option has a
   * leading control: its labels sit against the row padding instead of behind
   * 14px of empty space. Set it on a menu whose options *do* carry icons, so the
   * ones that don't line up with the ones that do — an unindented row beside an
   * indented one is the visible bug this prevents. It is also the way to line a
   * plain menu up with a neighbouring icon-bearing one.
   *
   * A multi menu ignores it: checkbox rows spend the same inset on a real
   * control, so they already own the indent.
   */
  forceIndent?: boolean;
  /**
   * Sets the None row in muted ink, for a menu where clearing reads as an absence rather than
   * as a peer of the options. Only meaningful with `includeNoneOption`.
   */
  noneOptionMuted?: boolean;
};

export type SharedDropdownImplV2Props<T extends string | number> = SharedDropdownV2Props<T> & {
  /** Set by the public wrapper, never by a caller. */
  mode: DropdownV2Mode;
  /**
   * The field's visible label, rendered with a `for` association.
   *
   * Optional because plenty of fields are labelled by surrounding copy instead —
   * a heading, a table column, a sentence the control sits inside. Omit it in
   * that case and pass `triggerAriaLabel`: forcing a label here only produced
   * two bad states, an empty one nobody wanted, or a second associated label
   * that concatenated with the real one. A field with neither has no accessible
   * name at all, which is worse than either.
   *
   * `hideLabel` keeps the association and the `for` target while hiding the text
   * visually, so it only means anything alongside a `label`.
   */
  label?: string;
  hideLabel?: boolean;
  description?: string;
  /**
   * Render only the option rows near the viewport instead of every filtered row.
   * Grouping, group titles, and the separators between groups all still work.
   *
   * Opt-in because it costs a measurement pass and an absolutely positioned row
   * per option, which is only worth it once the filtered list runs to hundreds of
   * rows — all features, all resolvers, every IANA timezone — and not before.
   */
  virtualized?: boolean;
  /**
   * Replaces the built-in case-insensitive substring search, which uses Fuse
   * scores to rank matches across each option's label and `searchMeta`. Receives
   * the full option list and the current query, and returns the entries to show
   * *in display order* — so a caller can rank by recency, usage, or another
   * domain-specific signal. Only called for a non-empty query.
   */
  /** Server search: receives the open menu query; options are rendered without local filtering. */
  onSearchChange?: (query: string) => void;
  /** Notifies callers when the menu opens or closes, for refreshing remote options. */
  onOpenChange?: (open: boolean) => void;
  filterEntries?: (options: readonly DropdownEntry<T>[], query: string) => DropdownEntry<T>[];
  /** Focus the input on mount, for a field that opens as its own overlay. */
  autoFocus?: boolean;
  testId?: string;
  listboxTestId?: string;
  options: readonly DropdownEntry<T>[];
};

// The leading icon and "default"/"text" trigger chrome live here rather than on
// the shared props because only the single trigger renders them: the multi
// trigger is a chip host with no room for a leading icon and no chevron to
// restyle. Accepting them there and dropping them silently is worse than not
// accepting them. `"icon"` is the exception - it replaces the trigger outright
// on either arity, so it is declared on both (paired with the shared
// `triggerIcon`) rather than only here.
export type SingleDropdownImplV2Props<T extends string | number> = SharedDropdownImplV2Props<T> & {
  variant?: "single";
  value?: T | null;
  onChange?: (value: T | null) => void;
  leftIcon?: IconName;
  /** Class for the trigger's leading icon. */
  leftIconClassName?: string;
  /**
   * Typography for the trigger's value or editable text. Defaults to the field
   * typography associated with `size`; use this when the trigger sits in a
   * non-form context whose type scale is owned by the surrounding UI.
   */
  triggerTextVariant?: TextVariant;
  /**
   * Trigger chrome. `"text"` renders it borderless and transparent so it reads
   * as inline text, with a surface fill on hover — matching SelectV2's variant
   * of the same name. `"icon"` replaces the trigger entirely; see `triggerIcon`.
   * Distinct from `variant`, which selects single vs multi.
   */
  triggerVariant?: "default" | "text" | "icon";
  /** Drops the trigger's chevron. Only the single trigger renders one. */
  hideChevron?: boolean;
  /** Treat this selected option value as placeholder text in the trigger. */
  placeholderValue?: T;
  /** Accessible label and placeholder for the searchable-select's in-menu search field. */
  searchPlaceholder?: string;
  clearable?: boolean;
  /**
   * A control rendered inside the trigger's frame after the chevron, sharing its border and
   * focus ring rather than sitting beside it — InputV2's prop of the same name. Buttons in it
   * do not open the menu.
   */
  trailingAction?: ReactNode;
};

export type MultiDropdownImplV2Props<T extends string | number> = SharedDropdownImplV2Props<T> & {
  variant: "multi";
  value: readonly T[];
  onChange?: (value: T[]) => void;
  /** Maximum number of selected values to render as removable chips. */
  displayLimit?: number;
  /** Keep the query while choosing several matching items. Defaults to clearing it after each pick. */
  clearSearchOnSelect?: boolean;
  /** The multi trigger has no other chrome to choose between, so `"icon"` is the only option. */
  triggerVariant?: "icon";
};

type DropdownNavigableItem<T extends string | number> = {
  id: string;
  option?: DropdownOption<T>;
  custom?: boolean;
  /** The searchable-select's "None" row, which commits null. */
  none?: boolean;
};

type DropdownMenuRow<T extends string | number> =
  | { kind: "title"; key: string; label: string; startsSection: boolean }
  | { kind: "option"; key: string; option: DropdownOption<T>; startsSection: boolean };

const getDropdownMenuRowKey = <T extends string | number>(row: DropdownMenuRow<T>) => row.key;

const optionLabelFontSize: Record<DropdownV2Size, number> = { sm: 12, md: 13 };
const optionSubtitleFontSize: Record<DropdownV2Size, number> = { sm: 11, md: 12 };
const titleLabelFontSize: Record<DropdownV2Size, number> = { sm: 11, md: 12 };
const optionAccessoryWidth: Record<DropdownV2Size, number> = { sm: 14, md: 16 };
const widthScaffoldLeftSlotClass: Record<DropdownV2Size, string> = {
  sm: "min-w-3.5",
  md: "min-w-4",
};
const OPTION_ROW_HORIZONTAL_PADDING = 14;
const OPTION_ROW_GAP = 8;

const estimateDropdownMenuRowWidth = <T extends string | number>(
  row: DropdownMenuRow<T>,
  size: DropdownV2Size,
  multi: boolean,
  reserveLeftSlot: boolean,
) => {
  if (row.kind === "title") {
    return (
      staticEstimateSVGTextWidth(row.label, titleLabelFontSize[size]) +
      OPTION_ROW_HORIZONTAL_PADDING
    );
  }

  const { option } = row;
  const subtitle =
    typeof option.subtitle === "string" || typeof option.subtitle === "number"
      ? String(option.subtitle)
      : "";
  const labelWidth = staticEstimateSVGTextWidth(option.label, optionLabelFontSize[size]);
  const subtitleWidth = staticEstimateSVGTextWidth(subtitle, optionSubtitleFontSize[size]);
  const leftAffordanceCount =
    Number(multi) +
    Number(
      option.leftAccessory != null ||
        (!multi && option.leftIcon != null) ||
        (!multi && reserveLeftSlot),
    );
  const rightAffordanceCount =
    Number(option.rightAccessory != null) + Number(option.rightLink != null);
  const labelLineWidth =
    labelWidth +
    OPTION_ROW_HORIZONTAL_PADDING +
    (leftAffordanceCount + rightAffordanceCount) * (optionAccessoryWidth[size] + OPTION_ROW_GAP);

  // A single-select subtitle spans the full row below the label line, so its
  // width is independent of the label's leading/trailing affordances. A multi
  // row keeps its checkbox and accessories beside the two-line text column.
  const subtitleAffordanceCount = multi ? leftAffordanceCount + rightAffordanceCount : 0;
  const subtitleLineWidth =
    subtitleWidth +
    OPTION_ROW_HORIZONTAL_PADDING +
    subtitleAffordanceCount * (optionAccessoryWidth[size] + OPTION_ROW_GAP);

  return Math.max(labelLineWidth, subtitleLineWidth);
};

// MenuItemV2's default row height at each size.
const optionRowHeight: Record<DropdownV2Size, number> = { sm: 28, md: 32 };

// A row with a subtitle is at least this tall, and taller once the subtitle
// wraps. Not per-size: the two-line row's floor is the same at both, because its
// label line box is 16px either way and only the subtitle's type scale differs.
// The floor rather than an average, so the estimate never overstates a list's
// length — an over-long track that shrinks as you scroll reads as a glitch, where
// a short one that grows reads as more content arriving.
const GROWABLE_VIRTUAL_ROW_ESTIMATE = 46;

export type DropdownImplV2Props<T extends string | number> =
  | SingleDropdownImplV2Props<T>
  | MultiDropdownImplV2Props<T>;

const singleContainerSizeClass: Record<DropdownV2Size, string> = {
  sm: "h-7 pl-cds-8 gap-cds-6",
  md: "h-8 pl-cds-8 gap-cds-8",
};

const multiContainerSizeClass: Record<DropdownV2Size, string> = {
  sm: "min-h-8 p-cds-6 gap-cds-6",
  md: "min-h-8 p-cds-6 gap-cds-6",
};

// Square, matching the bordered single/multi trigger's height at each size, so
// an icon trigger sits flush next to one of the other variants in a toolbar.
const iconTriggerSizeClass: Record<DropdownV2Size, string> = {
  sm: "h-7 w-7",
  md: "h-8 w-8",
};

const leftIconSize: Record<DropdownV2Size, IconSize> = {
  sm: "sm",
  md: "md",
};

const fieldTextVariant: Record<DropdownV2Size, TextVariant> = {
  sm: "sm",
  md: "md",
};

const errorTextVariant: Record<DropdownV2Size, TextVariant> = {
  sm: "xs",
  md: "sm",
};

const chipTextVariant: Record<DropdownV2Size, TextVariant> = {
  sm: "sm",
  md: "md",
};

const chipHeightClass: Record<DropdownV2Size, string> = {
  sm: "h-5",
  md: "h-6",
};

const multiInputHeightClass: Record<DropdownV2Size, string> = {
  sm: "h-5",
  md: "h-6",
};

const widthScaffoldTextClass: Record<DropdownV2Size, string> = {
  sm: "text-cds-sm",
  md: "text-cds-md",
};

const widthScaffoldTitleTextClass: Record<DropdownV2Size, string> = {
  sm: "text-cds-xs",
  md: "text-cds-sm",
};

const WIDTH_SCAFFOLD_STATE: DropdownOptionState = { active: false, selected: false };

/**
 * Absolutely positioned virtual rows cannot contribute to a content-width
 * menu's intrinsic size. Keep one representative row in normal flow at zero
 * height so the menu still hugs its widest label. The proxy deliberately owns
 * no listbox semantics or focusable chrome; `inert` also contains anything a
 * caller-provided renderer or accessory happens to return.
 */
const DropdownMenuWidthScaffold = <T extends string | number>({
  row,
  size,
  multi,
  reserveLeftSlot,
  itemRenderer,
}: {
  row: DropdownMenuRow<T>;
  size: DropdownV2Size;
  multi: boolean;
  reserveLeftSlot: boolean;
  itemRenderer: SharedDropdownV2Props<T>["itemRenderer"];
}) => {
  if (row.kind === "title") {
    return (
      <div
        ref={(node) => node?.setAttribute("inert", "")}
        aria-hidden={true}
        data-dropdown-width-scaffold={""}
        className={"pointer-events-none invisible h-0 w-max overflow-hidden"}
      >
        <div
          className={classNames(
            "flex w-max items-center whitespace-nowrap pl-cds-8 pr-cds-6 py-cds-4 font-medium",
            widthScaffoldTitleTextClass[size],
          )}
        >
          {row.label}
        </div>
      </div>
    );
  }

  const { option } = row;
  const labelContent = itemRenderer?.(option, WIDTH_SCAFFOLD_STATE);
  const leftAccessory = option.leftAccessory?.(WIDTH_SCAFFOLD_STATE);
  const rightAccessory = option.rightAccessory?.(WIDTH_SCAFFOLD_STATE);
  const trailingContent =
    option.rightLink == null ? (
      rightAccessory
    ) : (
      <span className="flex items-center gap-cds-8">
        {rightAccessory}
        <span aria-hidden className="h-cds-16 w-cds-16 shrink-0" />
      </span>
    );
  const leftContent =
    leftAccessory != null ? (
      leftAccessory
    ) : option.leftIcon != null ? (
      <Icon name={option.leftIcon} size={leftIconSize[size]} />
    ) : null;

  const label = (
    <span className={classNames("shrink-0", labelContent == null && "font-medium")}>
      {labelContent ?? option.label}
    </span>
  );
  const subtitle =
    option.subtitle == null ? null : (
      <span className={"w-max shrink-0 text-cds-xs font-normal text-cds-ink-muted"}>
        {option.subtitle}
      </span>
    );

  const leftSlot =
    leftContent != null || reserveLeftSlot ? (
      <span
        className={classNames(
          "flex h-cds-16 shrink-0 items-center justify-center leading-none",
          widthScaffoldLeftSlotClass[size],
        )}
      >
        {leftContent}
      </span>
    ) : null;

  const content = multi ? (
    <div
      className={classNames(
        "flex w-max items-start gap-cds-8 whitespace-nowrap pl-cds-8 pr-cds-6 py-cds-6",
        widthScaffoldTextClass[size],
      )}
    >
      <CheckboxV2Control />
      {leftAccessory != null && (
        <span className={"flex h-cds-16 shrink-0 items-center justify-center leading-none"}>
          {leftAccessory}
        </span>
      )}
      <span className={"flex w-max shrink-0 flex-col gap-cds-2"}>
        {label}
        {subtitle}
      </span>
      {trailingContent != null && (
        <span className={"flex h-cds-16 shrink-0 items-center leading-none"}>
          {trailingContent}
        </span>
      )}
    </div>
  ) : (
    <div
      className={classNames(
        "flex w-max items-start whitespace-nowrap pl-cds-8 pr-cds-6 py-cds-6",
        widthScaffoldTextClass[size],
      )}
    >
      <span className={"flex w-max shrink-0 flex-col gap-cds-2"}>
        <span className={"flex w-max items-center gap-cds-8"}>
          {leftSlot}
          {label}
          {trailingContent != null && (
            <span className={"flex h-cds-16 shrink-0 items-center leading-none"}>
              {trailingContent}
            </span>
          )}
        </span>
        {subtitle}
      </span>
    </div>
  );

  return (
    <div
      ref={(node) => node?.setAttribute("inert", "")}
      aria-hidden={true}
      data-dropdown-width-scaffold={""}
      className={"pointer-events-none invisible h-0 w-max overflow-hidden"}
    >
      {content}
    </div>
  );
};

// ------------------------------------------------------------------
// One option row.
//
// Its own memoized component rather than inline JSX. Moving the active row is a
// state change on the whole dropdown, so React re-renders every option element;
// MenuItemV2's `React.memo` is what keeps that from costing anything, and it only
// bails when a row's props all keep their identity. Built inline, the click
// closure and the `itemRenderer` result are fresh objects every render, which
// defeats the memo for every row at once — and a row render means another pass of
// tailwind-merge over its class lists.
//
// So the click handler and renderer calls belong in here, where they depend only
// on this row's own props. The parent's `onCommit` has to be identity-stable for
// that to hold; see `useEventCallback` where it is defined.
// ------------------------------------------------------------------

type DropdownOptionRowProps<T extends string | number> = {
  option: DropdownOption<T>;
  itemId: string;
  size: DropdownV2Size;
  multi: boolean;
  /**
   * Keyboard/ARIA cursor state. Pointer hover is a CSS surface on MenuItemV2 and
   * has no prop here, so it can neither move this nor reach the renderers.
   */
  active: boolean;
  /** Part of the current value: a checkbox for multi, a selected surface for single. */
  selected: boolean;
  /** The menu's `forceIndent`, applied to every row so the list shares one indent. */
  reserveLeftSlot: boolean;
  itemRenderer: SharedDropdownV2Props<T>["itemRenderer"];
  onCommit: (option: DropdownOption<T>) => void;
};

const DropdownOptionRowImpl = <T extends string | number>({
  option,
  itemId,
  size,
  multi,
  active,
  selected,
  reserveLeftSlot,
  itemRenderer,
  onCommit,
}: DropdownOptionRowProps<T>) => {
  const handleClick = useCallback(() => {
    onCommit(option);
  }, [onCommit, option]);

  const rendererState = { active, selected };
  // An option subtitle is descriptive copy at menu width, so it wraps rather
  // than clipping. Spread rather than passed inline: `wrapSubtitle` is
  // meaningless without a subtitle, and MenuItemV2's fixed-height variants pass
  // unrecognised props straight through to the DOM.
  const subtitleProps =
    option.subtitle == null ? null : { subtitle: option.subtitle, wrapSubtitle: true };

  const rightAccessory = option.rightAccessory?.(rendererState);
  const trailingContent =
    option.rightLink == null ? (
      rightAccessory
    ) : (
      <span className="flex items-center gap-cds-8">
        {rightAccessory}
        <span aria-hidden className="h-cds-16 w-cds-16 shrink-0" />
      </span>
    );
  const row = (
    <MenuItemV2
      data-testid={option.testId}
      id={itemId}
      role="option"
      tabIndex={-1}
      size={size}
      variant={multi ? "checkbox" : subtitleProps == null ? "default" : "two-line"}
      label={option.label}
      {...(option.rightLink == null ? {} : { "aria-label": option.label })}
      labelContent={itemRenderer?.(option, rendererState)}
      {...subtitleProps}
      leftAccessory={option.leftAccessory?.(rendererState)}
      rightAccessory={trailingContent}
      alignAccessoriesToLabelLine
      {...(multi
        ? { checked: selected }
        : {
            leftIcon: option.leftIcon,
            reserveLeftSlot,
            selected,
            hideSelectedIndicator: true,
          })}
      active={active}
      disabled={option.disabled}
      onClick={handleClick}
    />
  );
  if (option.rightLink == null) return row;

  return (
    <div role="presentation" className="group/dropdown-option relative">
      {row}
      <ChalkLink
        href={option.rightLink.href}
        aria-label={option.rightLink.label}
        data-dropdown-option-link
        tabIndex={active ? 0 : -1}
        openInNewTab={option.rightLink.openInNewTab}
        showOpenInNewTabIcon={false}
        className={classNames(
          "absolute right-cds-6 inline-flex h-cds-16 w-cds-16 items-center justify-center",
          size === "sm" ? "top-cds-6" : "top-cds-8",
          option.rightLink.showOnFocusOnly &&
            "pointer-events-none opacity-0 focus:pointer-events-auto focus:opacity-100",
          !option.rightLink.showOnFocusOnly &&
            option.rightLink.showOnHover &&
            !active &&
            "pointer-events-none opacity-0 group-hover/dropdown-option:pointer-events-auto group-hover/dropdown-option:opacity-100 group-focus-within/dropdown-option:pointer-events-auto group-focus-within/dropdown-option:opacity-100",
        )}
        onMouseDown={(event) => {
          // Keep input focus so blur cannot close the dropdown before the link's click.
          event.preventDefault();
        }}
      >
        <Icon name="externalLink" size="sm" />
      </ChalkLink>
    </div>
  );
};

// React.memo erases the generic parameter, so the cast puts the original
// signature back. The runtime wrapper — the whole point — is unaffected.
const DropdownOptionRow = React.memo(DropdownOptionRowImpl) as typeof DropdownOptionRowImpl;

// Floor for the bordered trigger, so a narrow flex/grid parent cannot squeeze it
// down to just its chevron. 112px is the narrowest deliberate caller in the app
// (`AgentTemplateBuilderPageContent`'s `w-28` wrapper), so it is the widest floor
// that breaks no existing layout while still leaving ~76px for text.
const TRIGGER_MIN_WIDTH_CLASS = "min-w-28";

const isSelectedValue = <T extends string | number>(values: readonly T[], candidate: T) => {
  return values.some((value) => Object.is(value, candidate));
};

const getNextMultiValue = <T extends string | number>(values: readonly T[], candidate: T) => {
  if (isSelectedValue(values, candidate)) {
    return values.filter((value) => !Object.is(value, candidate));
  }

  return [...values, candidate];
};

export function DropdownImplV2<T extends string | number>(props: DropdownImplV2Props<T>) {
  const {
    mode,
    // sm, matching SelectMenuV2's own default. A dropdown that defaulted to a
    // different size depending on which of the two public components you reached
    // for was a trap, and the smaller one is the house default for form fields.
    size = "sm",
    label,
    hideLabel = false,
    required = false,
    description,
    labelTrailingAction,
    error,
    disabled: disabledProp = false,
    menuClassName,
    loading = false,
    triggerAriaLabel,
    placeholder = props.variant === "multi"
      ? "Add item..."
      : // Only the combobox's trigger is typed into; both select-shaped triggers
        // are buttons, searchable or not.
        props.mode.kind === "combobox"
        ? "Search..."
        : "Select...",
    emptyMessage = "No results",
    virtualized = false,
    itemRenderer,
    valueRenderer,
    selectedOptions,
    filterEntries,
    onSearchChange,
    onOpenChange,
    autoFocus = false,
    onBlur,
    onScrollBottom,
    loadingMore = false,
    menuFooter,
    menuWidth = "content",
    menuPlacement = "bottom-start",
    className,
    containerClassName,
    forceIndent = false,
    noneOptionMuted = false,
    testId,
    listboxTestId,
    options,
    triggerVariant = "default",
    triggerIcon,
    triggerRef,
    menuZIndex = ZIndex.POPOVER,
  } = props;

  // The mode is the only thing that distinguishes the two public components, so
  // it is unpacked once here and the rest of the file reads these three.
  // `searchable` means the *trigger* is a text field. A searchable-select
  // filters too, but through a search row inside the menu, so its trigger stays
  // the select-mode button.
  const searchable = mode.kind === "combobox";
  const filtersInMenu = mode.kind === "searchable-select";
  const filters = searchable || filtersInMenu;
  // "icon" replaces the trigger with a bare button, which cannot also be the
  // text field a combobox's trigger has to be - so it only applies to a
  // select-shaped trigger (plain select or searchable-select).
  const isIconTrigger = triggerVariant === "icon" && !searchable;
  const allowCustomValue = mode.kind === "combobox" && mode.allowCustomValue;
  const customValueMaxLength = mode.kind === "combobox" ? mode.customValueMaxLength : null;
  const commitCustomValueOnBlur =
    allowCustomValue && mode.kind === "combobox" && mode.commitCustomValueOnBlur;
  const includeNoneOption = mode.kind !== "combobox" && mode.includeNoneOption;

  // A loading field is also a disabled one: the trigger is a skeleton, so there
  // is nothing meaningful to focus, open, or type into. This is also what makes
  // the menu unreachable while loading — the render is gated on `!disabled` — so
  // nothing below needs its own loading branch.
  const disabled = disabledProp || loading;

  // Value position is always selected and is never the keyboard cursor.
  const renderValue = valueRenderer ?? itemRenderer;
  const renderValueState = { active: false, selected: true } as const;

  const variant = props.variant ?? "single";
  const isMulti = variant === "multi";
  const singleProps = !isMulti ? (props as SingleDropdownImplV2Props<T>) : null;
  // Single-only chrome, read off the narrowed props rather than the union.
  const leftIconClassName = singleProps?.leftIconClassName;
  const hideChevron = singleProps?.hideChevron ?? false;
  const placeholderValue = singleProps?.placeholderValue;
  const searchPlaceholder = singleProps?.searchPlaceholder ?? "Search";
  const triggerTextVariant = singleProps?.triggerTextVariant ?? fieldTextVariant[size];
  const trailingAction = singleProps?.trailingAction;
  const multiProps = isMulti ? (props as MultiDropdownImplV2Props<T>) : null;
  const singleValue = singleProps?.value ?? null;
  const multiValue = multiProps?.value ?? [];
  // Whether the icon trigger's fill should read as "a value is selected",
  // regardless of arity.
  const iconTriggerHasValue = isMulti ? multiValue.length > 0 : singleValue != null;
  const displayedMultiValue =
    multiProps?.displayLimit == null
      ? multiValue
      : multiValue.slice(0, Math.max(0, multiProps.displayLimit));
  const hiddenMultiValueCount = multiValue.length - displayedMultiValue.length;
  const isSingleClearable = !isMulti && (singleProps?.clearable ?? false);
  const valueOptions = useMemo(
    () => (selectedOptions == null ? options : [...options, ...selectedOptions]),
    [options, selectedOptions],
  );
  const selectedSingleOption = findDropdownOption(valueOptions, (option) =>
    Object.is(option.value, singleValue),
  );
  const selectedSingleLabel =
    selectedSingleOption?.label ?? (singleValue != null ? String(singleValue) : "");
  const selectedSingleValueIsPlaceholder =
    placeholderValue != null && Object.is(singleValue, placeholderValue);
  // A selected option can supply its own leading content. Keep an explicit
  // valueRenderer authoritative: callers that replace the trigger body own its
  // complete layout, while the default trigger mirrors the option row.
  const selectedSingleLeftAccessory =
    !isMulti && !searchable && valueRenderer == null
      ? selectedSingleOption?.leftAccessory?.(renderValueState)
      : undefined;
  const amplitudeSelectionChange = useAmplitudeOnClick({
    event: AmplitudeEvent.DESIGN_SYSTEM_SELECTION_CHANGE,
    component: mode.kind === "combobox" ? "ComboboxV2" : "SelectMenuV2",
    extraProperties: {
      label,
      data_test_id: testId,
    },
  });

  const getSelectionDetails = (values: readonly T[]) => ({
    labels: values.map(
      (value) =>
        findDropdownOption(valueOptions, (option) => Object.is(option.value, value))?.label ??
        String(value),
    ),
    ids: values.map(
      (value) => findDropdownOption(valueOptions, (option) => Object.is(option.value, value))?.id,
    ),
  });

  const trackSelectionChange = (
    nextValues: readonly T[],
    newLabel: string,
    newId: string | undefined,
    freeformSelection: boolean,
  ) => {
    const current = isMulti
      ? getSelectionDetails(multiValue)
      : {
          labels: selectedSingleLabel,
          ids: selectedSingleOption?.id,
        };
    const next = isMulti ? getSelectionDetails(nextValues) : { labels: newLabel, ids: newId };

    amplitudeSelectionChange({
      current_selection_label: current.labels,
      current_selection_id: current.ids,
      new_selection_label: next.labels,
      new_selection_id: next.ids,
      freeform_selection: freeformSelection,
    });
  };
  const canClearSingleValue =
    isSingleClearable && singleValue != null && singleProps?.onChange != null && !disabled;
  const clearSingleLabel = label ? `Clear ${label}` : "Clear selection";

  const autoId = useId();
  const id = `combobox-${autoId}`;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const listboxId = `${id}-listbox`;
  const ariaDescribedBy = [descriptionId, errorId].filter(Boolean).join(" ") || undefined;
  // An empty string means the same thing as omitting it; callers used "" for
  // "no label" before this prop was optional.
  const shouldRenderLabel = label != null && label !== "";

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // MenuContainerV2 owns `overflow-y-auto` and the max-height, so it — not the
  // floating wrapper `menuRef` points at — is the virtualizer's scroll element.
  const menuScrollRef = useRef<HTMLDivElement | null>(null);
  const menuScrollFadeRef = useRef<MenuScrollEndFadeHandle | null>(null);
  // Type-ahead buffer for the select-only mode. Refs, not state: only the keydown
  // handler reads them, and re-rendering on each keystroke would be wasted work.
  const typeaheadPrefixRef = useRef("");
  const typeaheadResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chipRemoveRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const [open, setOpen] = useState(false);
  const notifyOpenChange = useEventCallback((nextOpen: boolean) => onOpenChange?.(nextOpen));
  useEffect(() => {
    notifyOpenChange(open);
  }, [notifyOpenChange, open]);
  // Only the combobox trigger displays the committed label as its input value;
  // for the button-triggered modes the query is purely a filter.
  const [query, setQuery] = useState(searchable ? selectedSingleLabel : "");
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  useEffect(
    () =>
      subscribeToOtherDropdownMenuOpen(id, () => {
        setOpen(false);
        setQuery(searchable && !isMulti ? selectedSingleLabel : "");
      }),
    [id, isMulti, searchable, selectedSingleLabel],
  );

  useEffect(() => {
    if (open) {
      announceDropdownMenuOpen(id);
    }
  }, [id, open]);
  // A ceiling that holds a whole number of rows, so a capped menu never cuts a
  // row in half; the scrollbar and MenuScrollEndFade show that more follow.
  // Only the ceiling is aligned: once the viewport caps the menu lower, the cut
  // falls wherever the room runs out. A footer's height is caller content, so
  // there is no row grid to align to.
  const menuCeiling =
    menuFooter != null
      ? MENU_MAX_HEIGHT_BUDGET
      : fullRowsMenuMaxHeight({
          budget: MENU_MAX_HEIGHT_BUDGET,
          rowsTop:
            MENU_BORDER_WIDTH +
            MENU_SECTION_INSET +
            (filtersInMenu ? optionRowHeight[size] + MENU_SEARCH_DIVIDER_HEIGHT : 0),
          rowHeight: optionRowHeight[size],
          rowsBottom: MENU_SECTION_INSET + MENU_BORDER_WIDTH,
        });
  // The menu is rendered in a portal (fixed positioning) so it escapes any
  // ancestor clipping context. The shared hook anchors it, keeps it on-screen,
  // caps its height to the available viewport, and reports the trigger width
  // without adding a separate layout read.
  const { refs, floatingStyles, menuMaxHeight, menuMaxWidth, triggerWidth } = useDropdownFloatingV2(
    {
      open,
      placement: menuPlacement,
      strategy: "fixed",
      offset: MENU_TRIGGER_GAP,
      maxHeight: menuCeiling,
    },
  );

  // A type-ahead run ends the moment the user does anything else, not when its
  // timer happens to expire. Leaving a stale prefix behind makes the next Space
  // extend that prefix instead of selecting, and blocks a fresh run for up to
  // TYPEAHEAD_RESET_MS — so every path that ends a run clears the buffer.
  const resetTypeahead = useCallback(() => {
    typeaheadPrefixRef.current = "";
    if (typeaheadResetRef.current != null) {
      clearTimeout(typeaheadResetRef.current);
      typeaheadResetRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (typeaheadResetRef.current != null) {
        clearTimeout(typeaheadResetRef.current);
      }
    };
  }, []);

  // Covers every way the menu can close — Escape, the chevron, an outside click,
  // and a single-select commit — without each of them having to remember to.
  useEffect(() => {
    if (!open) {
      resetTypeahead();
    }
  }, [open, resetTypeahead]);

  const setMenuRefs = useCallback(
    (node: HTMLDivElement | null) => {
      menuRef.current = node;
      refs.setFloating(node);
    },
    [refs],
  );

  const handleMenuScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const remaining =
        menuScrollFadeRef.current?.update(event.currentTarget) ??
        event.currentTarget.scrollHeight -
          event.currentTarget.scrollTop -
          event.currentTarget.clientHeight;

      if (onScrollBottom == null || loadingMore) {
        return;
      }

      if (remaining <= SCROLL_BOTTOM_THRESHOLD_PX) {
        onScrollBottom();
      }
    },
    [loadingMore, onScrollBottom],
  );

  useEffect(() => {
    if (searchable && !isMulti && !open) {
      setQuery(selectedSingleLabel);
    }
  }, [isMulti, open, searchable, selectedSingleLabel]);

  // With commitCustomValueOnBlur, leaving the field commits typed text instead
  // of reverting it, so a field that reads like a text input (type, then click
  // elsewhere) keeps what the user sees. Text naming an option by label or value
  // selects that option, so a typed label never becomes a raw custom value.
  // Returns false when there is nothing new to commit. Both leave paths call it:
  // an outside mousedown closes the menu (and would revert the query) before
  // the field's blur fires.
  const commitTypedValue = useEventCallback((): boolean => {
    const typed = query.trim();
    if (typed === "" || typed === selectedSingleLabel) return false;
    const normalized = normalizeDropdownQuery(query);
    const match = findDropdownOption(
      options,
      (option) =>
        normalizeDropdownQuery(option.label) === normalized ||
        normalizeDropdownQuery(String(option.value)) === normalized,
    );
    const committed = match?.value ?? (typed as unknown as T);
    if (!Object.is(committed, singleValue)) {
      trackSelectionChange([committed], match?.label ?? typed, match?.id, match == null);
      singleProps?.onChange?.(committed);
    }
    setOpen(false);
    setQuery(match?.label ?? typed);
    return true;
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      // The menu lives in a portal outside rootRef, so check it explicitly.
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        if (commitCustomValueOnBlur && !isMulti && !disabled && commitTypedValue()) return;
        setOpen(false);
        setQuery(searchable && !isMulti ? selectedSingleLabel : "");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [
    commitCustomValueOnBlur,
    commitTypedValue,
    disabled,
    isMulti,
    open,
    searchable,
    selectedSingleLabel,
  ]);

  // When the menu is open and the query still equals the selected option's
  // label (i.e. the user opened the dropdown but hasn't typed yet), show the
  // full list rather than filtering down to just the current selection. As soon
  // as they type something different, normal filtering resumes. Without this, a
  // combobox with a preselected value opens showing only that one option.
  // A plain select never filters: its query is display text, not a filter, so
  // the full list always shows. The selected-label echo only exists for a
  // combobox, whose trigger input displays the committed label as its value; a
  // searchable-select's query starts empty and is only ever a filter.
  const effectiveQuery = !filters
    ? ""
    : searchable && !isMulti && open && query === selectedSingleLabel
      ? ""
      : query;
  useEffect(() => {
    onSearchChange?.(open ? effectiveQuery : "");
  }, [effectiveQuery, onSearchChange, open]);

  const filteredEntries = useMemo(() => {
    // Closed dropdowns only need their trigger value. Preparing the complete filtered and grouped
    // menu here makes a form with many comboboxes repeat the work once per field, even though none
    // of those menus can be seen. This is especially costly for large feature lists populated by
    // offline-query reruns.
    if (!open) {
      return [];
    }

    if (onSearchChange != null) return [...options];

    // An empty query always shows everything, so filterEntries never has to
    // special-case it and can't accidentally hide the whole list on open.
    if (filterEntries == null || effectiveQuery.trim() === "") {
      return filterDropdownEntries(options, effectiveQuery);
    }

    return filterEntries(options, effectiveQuery);
  }, [effectiveQuery, filterEntries, onSearchChange, open, options]);
  const sections = useMemo(
    () => (open ? groupDropdownSections(filteredEntries) : []),
    [filteredEntries, open],
  );
  const hasResults = sections.some((section) => section.options.length > 0);
  const trimmedQuery = query.trim();
  const normalizedExactQuery = normalizeDropdownQuery(query);
  const hasExistingCustomValueMatch =
    open &&
    normalizedExactQuery !== "" &&
    (hasExactDropdownOptionMatch(options, normalizedExactQuery) ||
      (isMulti
        ? multiValue.some((value) => normalizeDropdownQuery(String(value)) === normalizedExactQuery)
        : singleValue != null &&
          normalizeDropdownQuery(String(singleValue)) === normalizedExactQuery));
  const hasCustomValueOption =
    open && allowCustomValue && trimmedQuery !== "" && !hasExistingCustomValueMatch;
  const customItemId = `${id}-custom`;
  const noneItemId = `${id}-none`;
  const selectedSingleItemId =
    selectedSingleOption == null ? null : `${id}-option-${selectedSingleOption.id}`;

  // Flattens the grouped sections into the single index space the virtualizer
  // needs. `startsSection` lets that path draw the real MenuSectionV2 separator
  // that the plain path gets from its nested sections.
  const menuRows = useMemo<DropdownMenuRow<T>[]>(() => {
    if (!virtualized) {
      return [];
    }

    return sections.flatMap((section, sectionIndex) => {
      const startsSection = sectionIndex > 0;
      // An empty title is an untitled section, as the plain path treats it: a separator, not a
      // blank heading row.
      const title = section.title;
      const untitled = title == null || title === "";
      const optionRows = section.options.map(
        (option, optionIndex): DropdownMenuRow<T> => ({
          kind: "option",
          key: option.id,
          option,
          startsSection: startsSection && untitled && optionIndex === 0,
        }),
      );

      return untitled
        ? optionRows
        : [
            {
              kind: "title" as const,
              key: `title-${sectionIndex}-${title}`,
              label: title,
              startsSection,
            },
            ...optionRows,
          ];
    });
  }, [sections, virtualized]);

  const widthScaffoldRows = useMemo(() => {
    if (!virtualized || menuWidth !== "content") {
      return [];
    }

    const widestEstimatedRow = menuRows.reduce<DropdownMenuRow<T> | null>((widest, row) => {
      if (widest == null) {
        return row;
      }

      return estimateDropdownMenuRowWidth(row, size, isMulti, forceIndent) >
        estimateDropdownMenuRowWidth(widest, size, isMulti, forceIndent)
        ? row
        : widest;
    }, null);

    if (widestEstimatedRow == null) {
      return [];
    }

    // Arbitrary subtitle nodes have no trustworthy static width. Measure those
    // candidates in the DOM as well; primitive-only rows keep the one-proxy fast
    // path, which matters for the thousands-row virtualized case. Accessory slots
    // are accounted for by the estimate, and the chosen row renders their real
    // nodes in the scaffold.
    const rowsNeedingDomMeasurement = menuRows.filter(
      (row) =>
        row.kind === "option" &&
        row.option.subtitle != null &&
        typeof row.option.subtitle !== "string" &&
        typeof row.option.subtitle !== "number",
    );

    return [widestEstimatedRow, ...rowsNeedingDomMeasurement].filter(
      (row, index, rows) => rows.findIndex((candidate) => candidate.key === row.key) === index,
    );
  }, [forceIndent, isMulti, menuRows, menuWidth, size, virtualized]);

  // The virtualizer measures every row it mounts, so subtitles work regardless of
  // what this says — it only sets the height assumed for rows that have never been
  // on screen, which is what sizes the scroll track. Left at the fixed row height,
  // a list of growable rows understates its own length by more than a third and
  // stretches under the thumb as you scroll it.
  const virtualItemHeight = menuRows.some(
    (row) => row.kind === "option" && row.option.subtitle != null,
  )
    ? GROWABLE_VIRTUAL_ROW_ESTIMATE
    : optionRowHeight[size];

  const scrollVirtualRowIndex = useMemo(() => {
    // Search keeps its input focused and opens without an active suggestion,
    // but the committed selection should still be brought into view. Once the
    // user navigates, the active row takes over as the scroll target.
    const scrollItemId = activeItemId ?? (open && filtersInMenu ? selectedSingleItemId : null);
    if (scrollItemId == null) {
      return undefined;
    }

    const index = menuRows.findIndex(
      (row) => row.kind === "option" && `${id}-option-${row.option.id}` === scrollItemId,
    );
    return index >= 0 ? index : undefined;
  }, [activeItemId, filtersInMenu, id, menuRows, open, selectedSingleItemId]);

  const navigableItems = useMemo<DropdownNavigableItem<T>[]>(() => {
    const items: DropdownNavigableItem<T>[] = sections.flatMap((section) =>
      section.options
        .filter((option) => !option.disabled)
        .map((option) => ({
          id: `${id}-option-${option.id}`,
          option,
        })),
    );

    // First, matching its render position ahead of the options. It never
    // filters out: clearing is orthogonal to whatever is being searched for.
    if (includeNoneOption) {
      items.unshift({ id: noneItemId, none: true });
    }

    if (hasCustomValueOption) {
      items.push({
        id: customItemId,
        custom: true,
      });
    }

    return items;
  }, [customItemId, hasCustomValueOption, id, includeNoneOption, noneItemId, sections]);

  const focusInput = () => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  };

  const menuSearchInputRef = useRef<HTMLInputElement | null>(null);

  // A searchable-select types into the menu, so opening it moves focus to the
  // search row — however the menu opened (click, keyboard, a typed character),
  // the next keystroke belongs to the filter. Focused from the ref callback, not
  // an effect on `open`: FloatingPortal mounts its children a commit after the
  // menu opens, so an open-keyed effect fires while this ref is still null.
  // Closing paths that should restore trigger focus do so themselves — an
  // outside click must not steal focus back.
  // Guards the trigger's blur handler during the deliberate trigger → search-row
  // focus handoff. The blur fires synchronously inside focus(), at a moment when
  // neither relatedTarget nor document.activeElement reliably names the search
  // input yet — jsdom always, browsers in edge cases — so without the flag the
  // handoff reads as a blur-out and closes the menu it just opened.
  const focusHandoffRef = useRef(false);

  const attachMenuSearchInput = useCallback(
    (node: HTMLInputElement | null) => {
      menuSearchInputRef.current = node;
      if (node != null) {
        focusHandoffRef.current = true;
        node.focus();
        focusHandoffRef.current = false;

        // FloatingPortal mounts its contents after the open-state commit, so an
        // effect keyed only on `open` runs before a non-virtual selected row
        // exists. The search input ref fires once that portal content is mounted.
        if (!virtualized && selectedSingleItemId != null) {
          document.getElementById(selectedSingleItemId)?.scrollIntoView?.({
            block: "nearest",
          });
        }
      }
    },
    [selectedSingleItemId, virtualized],
  );

  const focusChipRemoveButton = (index: number) => {
    chipRemoveRefs.current[index]?.focus();
  };

  const openMenu = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  // The query the active row was last chosen for. Surviving the filter is not
  // enough to stay active: a new query means the user is looking at a different
  // list, and Enter has to commit something they can see matching what they typed.
  const activeQueryRef = useRef("");

  // Filtering renders a different list immediately. Normalize its keyboard
  // cursor before paint so the old row cannot flash active for one frame.
  useLayoutEffect(() => {
    const queryChanged = activeQueryRef.current !== effectiveQuery;
    activeQueryRef.current = effectiveQuery;

    if (!open || navigableItems.length === 0) {
      setActiveItemId(null);
      return;
    }

    const filtering = effectiveQuery.trim() !== "";

    setActiveItemId((current) => {
      if (!queryChanged && current && navigableItems.some((item) => item.id === current)) {
        return current;
      }

      // The in-menu search input owns focus when this popup opens. It follows
      // the manual-autocomplete pattern: suggestions remain available, but none
      // is the keyboard cursor until the user navigates into the list. A new
      // query clears the old cursor for the same reason — Enter must not commit
      // a suggestion the user has not actively reached.
      if (filtersInMenu) {
        return null;
      }

      // The "None" row never filters out and renders first, so under a live query
      // it would otherwise hold the active position and make Enter clear the value
      // the user typed to find. Arrow keys still reach it: they do not touch the
      // query, so they never re-run this.
      if (filtering) {
        return (navigableItems.find((item) => !item.none) ?? navigableItems[0]).id;
      }

      if (!isMulti && selectedSingleItemId) {
        const selectedItem = navigableItems.find((item) => item.id === selectedSingleItemId);
        if (selectedItem) {
          return selectedItem.id;
        }
      }

      // A multi-select stays open across toggles, so a cursor seated on open would
      // outline a row the user never reached for as long as the popup lives. Its
      // checkboxes already carry selection state; the cursor appears once arrow
      // keys enter the list.
      if (isMulti) {
        return null;
      }

      return navigableItems[0].id;
    });
  }, [effectiveQuery, filtersInMenu, isMulti, navigableItems, open, selectedSingleItemId]);

  useEffect(() => {
    if (!open || !activeItemId) {
      return;
    }

    // Virtualized option rows scroll by index instead, inside VirtualizedList:
    // the active one is often not mounted here — arrow-keying past the window is
    // exactly when it isn't — so there is no element to scroll to.
    //
    // The "None" and custom-value rows are the exception. They render outside the
    // virtualized list, so the index scroll never matches them, and this is the
    // only thing that can bring them into view: without it, arrowing onto one
    // while the list is scrolled leaves the active row off screen and Enter
    // commits something the user cannot see.
    const isOutsideVirtualRows = activeItemId === noneItemId || activeItemId === customItemId;
    if (virtualized && !isOutsideVirtualRows) {
      return;
    }

    // Optional call, not just optional chain on the element: jsdom does not
    // implement scrollIntoView, so an unguarded call throws in any consumer's
    // test rather than degrading. Scrolling is a nicety here — the active row is
    // already tracked by aria-activedescendant.
    document.getElementById(activeItemId)?.scrollIntoView?.({
      block: "nearest",
    });
  }, [activeItemId, customItemId, noneItemId, open, virtualized]);

  // A searchable-select holds focus in the menu's search row, which unmounts with
  // the menu — so every commit has to hand focus back to the trigger or it lands
  // on document.body and the tab order restarts from the top of the page. The
  // other modes never move focus off the trigger, so this is a no-op for them.
  // Deliberately not on the close paths that are not commits: an outside click
  // must not steal focus back to a field the user just left.
  const restoreTriggerFocusAfterCommit = () => {
    if (filtersInMenu) {
      focusInput();
    }
  };

  const handleSingleSelect = (option: DropdownOption<T>) => {
    if (disabled || option.disabled) {
      return;
    }

    trackSelectionChange([option.value], option.label, option.id, false);
    singleProps?.onChange?.(option.value);
    // The combobox input displays the committed label as its value; a button
    // trigger renders the label itself, so its query is only ever a filter and
    // must not survive into the next open.
    setQuery(searchable ? option.label : "");
    setOpen(false);
    restoreTriggerFocusAfterCommit();
  };

  // The searchable-select's clear path: commits null exactly like a real option
  // commit, so the menu closes and the filter resets the same way.
  const handleSelectNone = () => {
    if (disabled) {
      return;
    }

    trackSelectionChange([], "", undefined, false);
    singleProps?.onChange?.(null);
    setQuery("");
    setOpen(false);
    restoreTriggerFocusAfterCommit();
  };

  const handleSingleClear = () => {
    if (disabled || !singleProps || !isSingleClearable) {
      return;
    }

    trackSelectionChange([], "", undefined, false);
    singleProps.onChange?.(null);
    setQuery("");
    setOpen(false);
  };

  const moveActiveItem = (direction: "next" | "prev" | "first" | "last") => {
    if (navigableItems.length === 0) {
      return;
    }

    // Arrow/Home/End navigation abandons whatever was being typed, so the next
    // Space means "select this" rather than extending a prefix the user has
    // already navigated away from.
    resetTypeahead();

    openMenu();

    if (direction === "first" || direction === "last") {
      setActiveItemId(
        direction === "first" ? navigableItems[0].id : navigableItems[navigableItems.length - 1].id,
      );
      return;
    }

    setActiveItemId((current) => {
      if (!current) {
        // Search opens with no active suggestion, but an existing selection is
        // still the list's navigation anchor (and the row revealed on open).
        // The first arrow enters the list at that row; later arrows move from it.
        if (
          filtersInMenu &&
          effectiveQuery.trim() === "" &&
          selectedSingleItemId != null &&
          navigableItems.some((item) => item.id === selectedSingleItemId)
        ) {
          return selectedSingleItemId;
        }

        // None is a clear action, not a match for the live query. Starting
        // forward navigation on the first actual result keeps search useful;
        // ArrowUp and subsequent movement can still reach None normally.
        if (direction === "next" && effectiveQuery.trim() !== "") {
          return (navigableItems.find((item) => !item.none) ?? navigableItems[0]).id;
        }
        return direction === "prev"
          ? navigableItems[navigableItems.length - 1].id
          : navigableItems[0].id;
      }

      const currentIndex = navigableItems.findIndex((item) => item.id === current);
      if (currentIndex === -1) {
        return direction === "prev"
          ? navigableItems[navigableItems.length - 1].id
          : navigableItems[0].id;
      }

      const nextIndex =
        direction === "prev"
          ? (currentIndex - 1 + navigableItems.length) % navigableItems.length
          : (currentIndex + 1) % navigableItems.length;

      return navigableItems[nextIndex].id;
    });
  };

  const commitActiveItem = () => {
    const activeItem = navigableItems.find((item) => item.id === activeItemId);
    if (!activeItem) {
      if (hasCustomValueOption) {
        commitCustomValue();
      }
      return;
    }

    if (activeItem.custom) {
      commitCustomValue();
      return;
    }

    if (activeItem.none) {
      handleSelectNone();
      return;
    }

    if (!activeItem.option) {
      return;
    }

    if (isMulti) {
      handleMultiToggle(activeItem.option);
      return;
    }

    handleSingleSelect(activeItem.option);
  };

  const commitCustomValue = () => {
    if (!hasCustomValueOption || disabled) {
      return;
    }

    const customValue = trimmedQuery as unknown as T;

    if (isMulti) {
      const nextValues = isSelectedValue(multiValue, customValue)
        ? [...multiValue]
        : [...multiValue, customValue];
      if (!isSelectedValue(multiValue, customValue)) {
        trackSelectionChange(nextValues, trimmedQuery, undefined, true);
        multiProps?.onChange?.(nextValues);
      }
      setQuery("");
      focusInput();
      return;
    }

    trackSelectionChange([customValue], trimmedQuery, undefined, true);
    singleProps?.onChange?.(customValue);
    setQuery(trimmedQuery);
    setOpen(false);
  };

  const handleMultiToggle = (option: DropdownOption<T>) => {
    if (disabled || option.disabled) {
      return;
    }

    // A multi menu stays open after a toggle, so the close effect never fires here
    // and the buffer has to be cleared explicitly — otherwise the user cannot start
    // a new type-ahead until the previous run's timer expires.
    resetTypeahead();

    const nextValues = getNextMultiValue(multiValue, option.value);
    trackSelectionChange(nextValues, option.label, option.id, false);
    multiProps?.onChange?.(nextValues);
    if (multiProps?.clearSearchOnSelect !== false) setQuery("");
    focusInput();
  };

  // Identity-stable so DropdownOptionRow's memo can bail: a fresh closure per row
  // per render defeats it, which turns moving the active row into a re-render of
  // every option. It still reads the latest render's state, so the behaviour is
  // the same as calling the handler directly.
  const handleOptionCommit = useEventCallback((option: DropdownOption<T>) => {
    if (isMulti) {
      handleMultiToggle(option);
      return;
    }

    handleSingleSelect(option);
  });

  // Shared by the plain and virtualized menu bodies so the two can't drift apart
  // in selection state or keyboard-active outlining.
  const renderOptionRow = (option: DropdownOption<T>) => {
    const optionItemId = `${id}-option-${option.id}`;

    return (
      <DropdownOptionRow<T>
        key={option.id}
        option={option}
        itemId={optionItemId}
        size={size}
        multi={isMulti}
        active={activeItemId === optionItemId}
        selected={
          isMulti ? isSelectedValue(multiValue, option.value) : Object.is(singleValue, option.value)
        }
        itemRenderer={itemRenderer}
        reserveLeftSlot={forceIndent}
        onCommit={handleOptionCommit}
      />
    );
  };

  const removeChipAtIndex = (index: number, focusTarget: "input" | "chip") => {
    if (!isMulti || disabled || !multiProps) {
      return;
    }

    const nextValues = multiValue.filter((_, valueIndex) => valueIndex !== index);
    const removedOption = findDropdownOption(valueOptions, (option) =>
      Object.is(option.value, multiValue[index]),
    );
    trackSelectionChange(
      nextValues,
      removedOption?.label ?? String(multiValue[index]),
      removedOption?.id,
      removedOption == null,
    );
    multiProps.onChange?.(nextValues);

    requestAnimationFrame(() => {
      if (focusTarget === "input") {
        focusInput();
        return;
      }

      const nextIndex = index < nextValues.length ? index : index - 1;
      if (nextIndex >= 0) {
        focusChipRemoveButton(nextIndex);
        return;
      }

      focusInput();
    });
  };

  const handleSingleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.currentTarget.value);
    setOpen(true);
  };

  const handleContainerMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    if (target.closest("button")) {
      return;
    }

    // With no filter to type, the whole field is the dropdown affordance, so it
    // toggles like the chevron. Searchable controls stay open-only: clicking the
    // text area starts a filter, and collapsing out from under that is hostile.
    if (!searchable) {
      event.preventDefault();
      if (open) {
        closeMenu();
      } else {
        openMenu();
      }
      focusInput();
      return;
    }

    if (target === inputRef.current) {
      return;
    }

    event.preventDefault();
    openMenu();
    focusInput();
  };

  // Collapsing the list also normalizes the query: a leftover filter string would
  // otherwise survive into the next open and silently hide most of the options.
  const closeMenu = () => {
    setOpen(false);
    // The combobox single trigger displays the committed label as its input
    // value; everything else's query is purely a filter and resets to empty.
    setQuery(searchable && !isMulti ? selectedSingleLabel : "");
  };

  const {
    handleChipRemoveButtonKeyDown,
    handleMenuSearchKeyDown,
    handleMultiInputKeyDown,
    handleSearchableSelectTriggerKeyDown,
    handleSingleInputKeyDown,
  } = useDropdownKeyboardHandlers({
    activeItemId,
    closeMenu,
    commitActiveItem,
    displayedMultiValueLength: displayedMultiValue.length,
    filters,
    focusChipRemoveButton,
    focusInput,
    hasCustomValueOption,
    moveActiveItem,
    multiValueLength: multiValue.length,
    navigableItems,
    onRemoveLastMultiValue: () => {
      if (multiValue.length > 0) {
        removeChipAtIndex(multiValue.length - 1, "input");
      }
    },
    open,
    openMenu,
    query,
    removeChipAtIndex,
    setActiveItemId,
    setQuery,
    typeaheadPrefixRef,
    typeaheadResetRef,
  });

  // Becoming disabled while open only *hid* the popup, because the render is
  // gated on `open && !disabled`. The state stayed true, so aria-expanded kept
  // claiming the popup was open, and re-enabling the control made it reappear
  // with no user action. Close it for real on the transition instead.
  useEffect(() => {
    if (disabled && open) {
      closeMenu();
    }
    // closeMenu is re-created every render; the transition is what matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, open]);

  const toggleMenu = () => {
    if (open) {
      closeMenu();
    } else {
      openMenu();
    }

    focusInput();
  };

  // Button-backed selects wait for click so opening the portaled menu cannot
  // retarget the same activation to the trigger button. Input-backed comboboxes
  // toggle on mousedown because their release target has no click toggle.
  const handleChevronMouseDown = (event: React.MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (searchable) {
      toggleMenu();
    }
  };

  const handleChevronClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!searchable) {
      toggleMenu();
    }
  };

  const handleRootKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    const target = event.target as HTMLElement;
    if (target.closest("[data-dropdown-option-link]") != null) {
      if (event.key === "Tab" && event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        (menuSearchInputRef.current ?? inputRef.current)?.focus();
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        focusInput();
        closeMenu();
      } else if (event.key === "Tab") {
        // Restore the controller before native Tab advances beyond the closing portal.
        focusInput();
        closeMenu();
      }
      return;
    }
    if (
      event.key === "Tab" &&
      !event.shiftKey &&
      activeItemId != null &&
      (target === inputRef.current || target === menuSearchInputRef.current)
    ) {
      const link = document
        .getElementById(activeItemId)
        ?.parentElement?.querySelector<HTMLAnchorElement>("[data-dropdown-option-link]");
      if (link != null) {
        event.preventDefault();
        event.stopPropagation();
        link.focus();
      }
    }
  };

  const handleRootBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // The searchable-select's own trigger → search-row handoff; see the flag.
    if (focusHandoffRef.current) {
      return;
    }

    const nextFocusedElement = event.relatedTarget as Node | null;

    // The menu is portaled outside rootRef; keep it open when focus moves into a
    // menu item (e.g. clicking an option focuses its <button>) so the click can
    // register instead of the blur closing the menu first.
    if (
      nextFocusedElement &&
      (rootRef.current?.contains(nextFocusedElement) ||
        menuRef.current?.contains(nextFocusedElement))
    ) {
      return;
    }

    if (commitCustomValueOnBlur && !isMulti && !disabled && commitTypedValue()) {
      onBlur?.();
      return;
    }

    closeMenu();
    // After closeMenu, so a consumer that unmounts the field on blur (a palette
    // dismissing itself) doesn't leave an open menu behind.
    onBlur?.();
  };

  // The scrollable menu body, shared by both menu shapes below: the plain menu
  // scrolls the whole panel, while a searchable-select pins its search row and
  // scrolls only the listbox under it.
  const menuBody = (
    <>
      {includeNoneOption && (
        // Ahead of the options and never filtered out: clearing is orthogonal
        // to whatever is being searched for.
        <MenuSectionV2>
          <MenuItemV2
            id={noneItemId}
            role={"option"}
            tabIndex={-1}
            size={size}
            label={
              noneOptionMuted ? <span className={MUTED_NONE_OPTION_TEXT_CLASS}>None</span> : "None"
            }
            reserveLeftSlot={forceIndent}
            selected={singleValue == null}
            hideSelectedIndicator
            active={activeItemId === noneItemId}
            onClick={handleSelectNone}
          />
        </MenuSectionV2>
      )}
      {hasResults ? (
        <>
          {widthScaffoldRows.map((row) => (
            <DropdownMenuWidthScaffold
              key={`width-${row.key}`}
              row={row}
              size={size}
              multi={isMulti}
              reserveLeftSlot={forceIndent}
              itemRenderer={itemRenderer}
            />
          ))}
          {virtualized ? (
            <MenuSectionV2>
              <VirtualizedList
                items={menuRows}
                getKey={getDropdownMenuRowKey}
                getItemClassName={(row) =>
                  classNames(
                    row.startsSection &&
                      "border-t-[1px] border-cds-surface-active dark:border-zinc-800",
                  )
                }
                estimatedItemHeight={virtualItemHeight}
                scrollElementRef={menuScrollRef}
                scrollToIndex={scrollVirtualRowIndex}
                renderItem={(row) =>
                  row.kind === "title" ? (
                    <MenuItemV2 size={size} variant={"title"} label={row.label} />
                  ) : (
                    renderOptionRow(row.option)
                  )
                }
              />
            </MenuSectionV2>
          ) : (
            sections.map((section, index) => (
              <MenuSectionV2 key={`${section.title ?? "ungrouped"}-${index}`}>
                {section.title && (
                  <MenuItemV2 size={size} variant={"title"} label={section.title} />
                )}
                {section.options.map((option) => renderOptionRow(option))}
              </MenuSectionV2>
            ))
          )}
        </>
      ) : !hasCustomValueOption ? (
        <MenuSectionV2>
          <MenuItemV2 size={size} variant={"title"} label={emptyMessage} />
        </MenuSectionV2>
      ) : null}
      {hasCustomValueOption && (
        <MenuSectionV2>
          <MenuItemV2
            id={customItemId}
            role={"option"}
            tabIndex={-1}
            size={size}
            label={`Use "${trimmedQuery}"`}
            leftIcon={"add"}
            active={activeItemId === customItemId}
            onClick={commitCustomValue}
          />
        </MenuSectionV2>
      )}
    </>
  );

  const loadingMoreRow = loadingMore ? (
    <div
      className={classNames(
        "sticky bottom-0 z-[1] flex justify-center px-cds-8 py-cds-6",
        "border-t border-cds-surface-active bg-cds-surface-default",
        "dark:border-zinc-800 dark:bg-cds-elevated-dark",
      )}
    >
      <LoadingSpinnerWithTextV2
        aria-label={"Loading more options"}
        size={"xs"}
        text={"Loading more options…"}
      />
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      data-ds-v2={""}
      // A bordered field fills its parent, the way InputV2 and SelectV2 do: it is
      // a form control, and the parent owns its width. The text variant hugs its
      // content instead, since it renders as inline text in a toolbar or a
      // sentence, where stretching means nothing.
      //
      // `w-fit` rather than `w-auto` for the hugging case — the root is a block
      // element, so `width: auto` would still fill the parent.
      //
      // Either default is overridable from `className`: `classNames` is
      // tailwind-merge, so a caller's width replaces this one rather than racing
      // it in stylesheet order.
      className={classNames(
        triggerVariant === "text" || isIconTrigger ? "w-fit" : "w-full",
        className,
      )}
      onBlurCapture={handleRootBlur}
      onKeyDownCapture={handleRootKeyDownCapture}
    >
      {shouldRenderLabel && (
        <div className={hideLabel ? "sr-only" : undefined}>
          <FieldLabelV2
            label={label}
            required={required}
            description={description}
            size={size}
            htmlFor={id}
            descriptionId={descriptionId}
            trailingAction={labelTrailingAction}
          />
        </div>
      )}

      <div ref={refs.setReference} className={"relative"}>
        {loading && (
          // Overlays the trigger rather than replacing it, so the field keeps its
          // real geometry and nothing shifts when the data lands. The trigger
          // underneath goes `invisible` (not unmounted) for the same reason.
          <Skeleton
            aria-label={`Loading ${label || triggerAriaLabel || "options"}`}
            ariaHidden={false}
            className="absolute inset-0 z-[1] h-full w-full"
            radius="sm"
            role="status"
          />
        )}
        {isIconTrigger ? (
          // Same trigger regardless of arity: it never shows the value(s), only
          // whether there are any, so single vs multi has nothing to render
          // differently here. Selection itself still works via row toggling in
          // the menu (multi) or committing a row (single).
          <button
            ref={mergeRefs<HTMLButtonElement | null>(
              inputRef as unknown as React.MutableRefObject<HTMLButtonElement | null>,
              triggerRef ?? null,
            )}
            data-testid={testId}
            id={id}
            type={"button"}
            disabled={disabled}
            autoFocus={autoFocus}
            aria-label={triggerAriaLabel}
            role={filtersInMenu ? undefined : "combobox"}
            aria-haspopup={"listbox"}
            aria-expanded={open}
            aria-controls={listboxId}
            aria-invalid={error ? true : undefined}
            aria-required={required || undefined}
            aria-describedby={ariaDescribedBy}
            aria-activedescendant={!filtersInMenu && open ? activeItemId ?? undefined : undefined}
            onKeyDown={
              filtersInMenu ? handleSearchableSelectTriggerKeyDown : handleSingleInputKeyDown
            }
            onClick={() => {
              if (open) {
                closeMenu();
              } else {
                openMenu();
              }
            }}
            className={classNames(
              "flex shrink-0 items-center justify-center rounded-cds-6 outline outline-1 -outline-offset-1 transition-colors",
              loading && "invisible",
              iconTriggerSizeClass[size],
              iconTriggerHasValue
                ? "outline-cds-status-green-border text-cds-status-green-text dark:outline-cds-status-green-border-dark dark:text-cds-status-green-text-dark"
                : // A visible resting state, not just a hover affordance - this is a
                  // standalone icon button (e.g. a toolbar search trigger), not a form
                  // field chrome that only needs to read once focused.
                  "bg-cds-surface-default outline-cds-surface-border text-cds-ink dark:bg-transparent dark:outline-white/10 dark:text-zinc-400",
              disabled
                ? "cursor-not-allowed opacity-50"
                : "cursor-pointer hover:bg-cds-surface-active dark:hover:bg-white/[0.08]",
              containerClassName,
            )}
          >
            {triggerIcon != null && (
              <Icon name={triggerIcon} size={leftIconSize[size]} aria-hidden="true" />
            )}
          </button>
        ) : isMulti ? (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            className={classNames(
              "flex flex-wrap items-center content-start",
              loading && "invisible",
              "w-full rounded-cds-6 border-[1px]",
              "bg-cds-surface-default dark:bg-white/[0.04]",
              "transition-colors duration-75",
              !disabled && "shadow-cds-s has-[:focus-visible]:shadow-cds-focus",
              error ? "border-cds-error" : "border-cds-surface-border",
              "dark:!border-white/[0.08]",
              disabled
                ? "cursor-not-allowed bg-cds-surface-active dark:bg-white/[0.04]"
                : searchable
                  ? "cursor-text"
                  : "cursor-pointer",
              // The chips already wrap to new rows, so a squeezed multi trigger
              // grows tall and unreadable rather than merely narrow.
              TRIGGER_MIN_WIDTH_CLASS,
              multiContainerSizeClass[size],
              containerClassName,
            )}
            onMouseDown={handleContainerMouseDown}
          >
            {displayedMultiValue.map((selectedValue, index) => {
              const option = findDropdownOption(valueOptions, (candidate) =>
                Object.is(candidate.value, selectedValue),
              );

              return (
                <ComboboxChipV2
                  key={option?.id ?? `custom-${String(selectedValue)}`}
                  size={size}
                  custom={option == null}
                  disabled={disabled}
                  title={option?.tooltip ?? option?.label ?? String(selectedValue)}
                  removeButtonLabel={`Remove ${option?.label ?? String(selectedValue)}`}
                  removeButtonRef={(node) => {
                    chipRemoveRefs.current[index] = node;
                  }}
                  onRemoveButtonKeyDown={(event) => handleChipRemoveButtonKeyDown(event, index)}
                  onRemove={() => removeChipAtIndex(index, "input")}
                >
                  {option != null && renderValue != null
                    ? renderValue(option, renderValueState)
                    : option?.label ?? String(selectedValue)}
                </ComboboxChipV2>
              );
            })}
            {hiddenMultiValueCount > 0 && (
              <ComboboxChipV2 size={size} disabled={disabled}>
                {`+${hiddenMultiValueCount}`}
              </ComboboxChipV2>
            )}
            <input
              ref={inputRef}
              data-testid={testId}
              id={id}
              disabled={disabled}
              // The multi trigger stays an input even when not searchable — it
              // hosts the chips and their backspace/arrow handling — so it is made
              // read-only rather than swapped for a button the way the single
              // trigger is. Without this, `searchable={false}` still let you type.
              readOnly={!searchable}
              // Same reason as the single trigger: a field labelled by surrounding
              // copy has no `label` of its own, and the placeholder is cleared as
              // soon as a chip appears — so without this the control loses its
              // accessible name on first selection.
              aria-label={triggerAriaLabel}
              role={"combobox"}
              aria-expanded={open}
              aria-controls={listboxId}
              aria-invalid={error ? true : undefined}
              aria-required={required || undefined}
              aria-describedby={ariaDescribedBy}
              aria-autocomplete={searchable ? "list" : "none"}
              aria-activedescendant={open ? activeItemId ?? undefined : undefined}
              value={query}
              placeholder={multiValue.length === 0 ? placeholder : ""}
              maxLength={
                allowCustomValue && customValueMaxLength != null ? customValueMaxLength : undefined
              }
              onFocus={openMenu}
              // Selecting an option closes the menu but keeps focus here, so onFocus cannot reopen it
              // and the container's handler skips the input. Without this, the second click is inert.
              onMouseDown={openMenu}
              onChange={(event) => {
                // `readOnly` already blocks typing in a browser; this keeps the
                // invariant true for anything that sets the value directly.
                if (!searchable) {
                  return;
                }
                setQuery(event.currentTarget.value);
                setOpen(true);
              }}
              onKeyDown={handleMultiInputKeyDown}
              className={classNames(
                "flex-1 min-w-[72px] self-center leading-[16px]",
                "!p-0 !border-0 !bg-transparent !shadow-none",
                "focus:!ring-0 focus:!ring-offset-0 focus:!border-0",
                "focus:!outline-none focus:!shadow-none",
                "!text-cds-ink placeholder:!text-cds-ink-muted",
                "dark:!text-zinc-200 dark:placeholder:!text-zinc-500",
                "disabled:!text-cds-ink-muted dark:disabled:!text-zinc-500",
                "disabled:cursor-not-allowed dark:disabled:opacity-50",
                // The container's cursor does not reach here: an input carries its
                // own UA `cursor: auto`, which resolves to an I-beam over text
                // regardless of the parent. A select-mode multi trigger is not
                // typeable, so it must say so — the single trigger gets this free
                // by being a <button>, but this one stays an input to host chips.
                !searchable && "cursor-pointer",
                multiInputHeightClass[size],
                DesignSystemTypography[fieldTextVariant[size]],
              )}
            />
          </div>
        ) : (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            className={classNames(
              "flex items-center w-full rounded-cds-6 border-[1px]",
              loading && "invisible",
              "transition-colors duration-75",
              // Mirrors SelectV2's variant of the same name: chrome-less at rest,
              // the 1px border kept transparent so the box does not shift, and a
              // surface fill on hover instead of a focus ring.
              triggerVariant === "text"
                ? classNames(
                    "bg-transparent",
                    error ? "!border-cds-error" : "border-transparent dark:!border-transparent",
                    !disabled &&
                      "hover:bg-cds-surface-active dark:hover:bg-white/[0.08] has-[:focus-visible]:bg-cds-surface-active dark:has-[:focus-visible]:bg-white/[0.08]",
                  )
                : classNames(
                    "bg-cds-surface-default dark:bg-white/[0.04]",
                    // Keyed on focus-visible, not focus-within. The open menu
                    // preventDefaults its mousedown so the trigger keeps focus
                    // through a click — correct, but with focus-within the field
                    // stayed lit after picking an option, long after the menu
                    // closed. focus-visible lights it for keyboard focus only,
                    // which is how SelectV2 handles the same problem.
                    !disabled && "shadow-cds-s has-[:focus-visible]:shadow-cds-focus",
                    error ? "border-cds-error" : "border-cds-surface-border",
                    "dark:!border-white/[0.08]",
                    disabled && "bg-cds-surface-active dark:bg-white/[0.04]",
                    // `w-full` alone lets a flex or grid parent shrink the field
                    // past its own chrome — padding plus the chevron already eat
                    // ~36px, so a squeezed trigger shows a caret and nothing else.
                    // Deliberately not applied to the text variant, whose whole
                    // point is hugging inline content.
                    TRIGGER_MIN_WIDTH_CLASS,
                  ),
              disabled ? "cursor-not-allowed" : searchable ? "cursor-text" : "cursor-pointer",
              singleContainerSizeClass[size],
              canClearSingleValue ? "pr-px" : trailingAction != null ? "pr-0" : "pr-cds-6",
              containerClassName,
            )}
            onMouseDown={handleContainerMouseDown}
          >
            {selectedSingleLeftAccessory != null ? (
              <span
                className={classNames(
                  "flex h-cds-16 shrink-0 items-center justify-center leading-none",
                  disabled && "dark:opacity-50",
                )}
              >
                {selectedSingleLeftAccessory}
              </span>
            ) : singleProps?.leftIcon ? (
              <Icon
                name={singleProps.leftIcon}
                size={leftIconSize[size]}
                className={classNames(
                  "!text-cds-ink-muted shrink-0",
                  disabled && "dark:opacity-50",
                  leftIconClassName,
                )}
              />
            ) : null}
            {!searchable ? (
              // A non-editable single-select is a button, not a text field. Using
              // a read-only input instead would announce as "edit text" to a
              // screen reader, break every consumer that queries the trigger by
              // button role or by its label text, and need the caret suppressed by
              // hand. A button gets the right semantics and the right cursor for
              // free. Keyboard handling is the same handler as the input's, so
              // arrow keys and aria-activedescendant behave identically.
              <button
                ref={inputRef as unknown as React.Ref<HTMLButtonElement>}
                data-testid={testId}
                id={id}
                type={"button"}
                disabled={disabled}
                autoFocus={autoFocus}
                aria-label={triggerAriaLabel}
                // `combobox`, not the implicit `button` role: `button` does not
                // support aria-activedescendant, so a button announcing an active
                // descendant is an invalid focus model. This is the W3C
                // select-only combobox pattern — a combobox-roled trigger owning
                // aria-activedescendant over a listbox popup of options.
                //
                // A searchable-select is the exception: focus moves into the
                // menu's search row, which is the combobox and owns the active
                // descendant, so this trigger stays a plain disclosure button.
                role={filtersInMenu ? undefined : "combobox"}
                aria-haspopup={"listbox"}
                aria-expanded={open}
                aria-controls={listboxId}
                aria-invalid={error ? true : undefined}
                aria-required={required || undefined}
                aria-describedby={ariaDescribedBy}
                aria-activedescendant={
                  !filtersInMenu && open ? activeItemId ?? undefined : undefined
                }
                onKeyDown={
                  filtersInMenu ? handleSearchableSelectTriggerKeyDown : handleSingleInputKeyDown
                }
                // Toggles on click, not mousedown: a button's activation event is
                // click, which is also what keyboard Enter/Space synthesises and
                // what callers fire. The container handler cannot do this because
                // it bails on any `closest("button")` so the clear button and
                // chevron keep working — and the trigger is now a button itself.
                onClick={() => {
                  if (open) {
                    closeMenu();
                  } else {
                    openMenu();
                  }
                }}
                className={classNames(
                  "flex-1 min-w-0 truncate text-left",
                  "!p-0 !border-0 !bg-transparent !shadow-none focus:!outline-none",
                  selectedSingleLabel === "" || selectedSingleValueIsPlaceholder
                    ? "!text-cds-ink-muted dark:!text-zinc-500"
                    : "!text-cds-ink dark:!text-zinc-200",
                  disabled && "!text-cds-ink-muted dark:!text-zinc-500 cursor-not-allowed",
                  DesignSystemTypography[triggerTextVariant],
                )}
              >
                {selectedSingleOption != null && renderValue != null
                  ? renderValue(selectedSingleOption, renderValueState)
                  : selectedSingleLabel === ""
                    ? placeholder
                    : selectedSingleLabel}
              </button>
            ) : (
              <input
                ref={inputRef}
                data-testid={testId}
                id={id}
                disabled={disabled}
                autoFocus={autoFocus}
                aria-label={triggerAriaLabel}
                role={"combobox"}
                aria-expanded={open}
                aria-controls={listboxId}
                aria-invalid={error ? true : undefined}
                aria-required={required || undefined}
                aria-describedby={ariaDescribedBy}
                aria-autocomplete={"list"}
                aria-activedescendant={open ? activeItemId ?? undefined : undefined}
                value={query}
                placeholder={placeholder}
                maxLength={
                  allowCustomValue && customValueMaxLength != null
                    ? customValueMaxLength
                    : undefined
                }
                onFocus={openMenu}
                onMouseDown={openMenu}
                onChange={handleSingleInputChange}
                onKeyDown={handleSingleInputKeyDown}
                className={classNames(
                  "!p-0 !border-0 !bg-transparent !shadow-none",
                  "focus:!ring-0 focus:!ring-offset-0 focus:!border-0",
                  "focus:!outline-none focus:!shadow-none",
                  "flex-1 min-w-0",
                  "!text-cds-ink placeholder:!text-cds-ink-muted",
                  "dark:!text-zinc-200 dark:placeholder:!text-zinc-500",
                  "disabled:!text-cds-ink-muted dark:disabled:!text-zinc-500",
                  "disabled:cursor-not-allowed dark:disabled:opacity-50",
                  DesignSystemTypography[triggerTextVariant],
                )}
              />
            )}
            {canClearSingleValue ? (
              <IconButtonV2
                type={"button"}
                aria-label={clearSingleLabel}
                icon={"close"}
                size={size === "sm" ? "xs" : "sm"}
                variant={"tertiary"}
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleSingleClear}
                className={"shrink-0"}
              />
            ) : hideChevron ? null : (
              <Icon
                // The text variant reads as inline text, where a single down
                // caret is quieter; the bordered default keeps the up/down pair.
                // Same split as SelectV2, so a migrated trigger looks unchanged.
                name={triggerVariant === "text" ? "chevronDown" : "chevronsUpDown"}
                onMouseDown={handleChevronMouseDown}
                onClick={handleChevronClick}
                className={classNames(
                  "shrink-0",
                  disabled ? "cursor-not-allowed" : "cursor-pointer",
                  disabled ? "!text-zinc-300 dark:!text-zinc-500" : "!text-cds-ink-subtle",
                  disabled && "dark:opacity-50",
                )}
              />
            )}
            {trailingAction != null && (
              <div className={"-mr-px flex shrink-0 items-center"}>{trailingAction}</div>
            )}
          </div>
        )}

        {open && !disabled && (
          <FloatingPortal>
            <div
              ref={setMenuRefs}
              role={"presentation"}
              // Portaled out of the field's DOM subtree; MenuContainerV2 below
              // re-establishes the design-system typography/scoping, so this
              // wrapper only owns positioning, the width, and the stack layer
              // that lifts the menu above sibling cell content.
              style={{
                ...floatingStyles,
                width:
                  menuWidth === "content"
                    ? undefined
                    : menuWidth === "trigger"
                      ? triggerWidth
                      : menuWidth,
                // Only "content" needs a floor: it hugs the options, and a menu
                // narrower than the trigger it hangs off reads as a rendering
                // bug. "trigger" is already exactly that width, and an explicit
                // width is the caller's call — applying the floor there silently
                // widened a requested 280px menu to whatever the trigger was.
                minWidth:
                  menuWidth === "content" && triggerWidth != null
                    ? Math.min(triggerWidth, menuMaxWidth ?? Number.POSITIVE_INFINITY)
                    : undefined,
                maxWidth:
                  menuWidth === "content"
                    ? menuMaxWidth ?? `calc(100vw - ${MENU_VIEWPORT_MARGIN * 2}px)`
                    : undefined,
                zIndex: menuZIndex,
              }}
              onMouseDown={(event) => {
                // Keep focus on the input so selecting an option doesn't blur the
                // field (which would close the menu before the click registers).
                // Skip that when the mousedown lands on the search row's own input -
                // preventDefault there blocks the browser's native drag-to-select
                // and double-click-to-select-word behavior on text already focused
                // in that field, with nothing to protect since it can't blur itself.
                if (!(event.target instanceof HTMLInputElement)) {
                  event.preventDefault();
                }
              }}
            >
              {filtersInMenu ? (
                // The search row must not scroll away with the options, so the
                // panel is a fixed-height column: the row keeps its size and the
                // listbox below it owns the scrolling. The row's input is the
                // combobox here — it holds focus, tracks the active option with
                // aria-activedescendant, and names the sibling listbox that
                // descendant lives in, which is what lets the reference resolve.
                <MenuContainerV2
                  className={"flex min-h-0 flex-col"}
                  style={{ maxHeight: menuMaxHeight }}
                >
                  {/* The search row's flush divider reaches the container edges by cancelling a
                      MenuSectionV2's 3px inset with negative margins, so it needs that section
                      around it; unwrapped, the stroke overhangs the border. The section drops its
                      bottom inset because the divider already leaves 3px under the stroke, and the
                      first option group below drops its top inset for the same reason, so the
                      list sits 3px under the stroke as in every other searchable menu. */}
                  <MenuSectionV2 className={"shrink-0 pb-0"}>
                    <MenuItemV2
                      size={size}
                      variant={"search"}
                      label={searchPlaceholder}
                      searchValue={query}
                      onSearchChange={setQuery}
                      onSearchKeyDown={handleMenuSearchKeyDown}
                      searchInputRef={attachMenuSearchInput}
                      searchAriaActivedescendant={activeItemId ?? undefined}
                      searchListboxId={listboxId}
                      showBottomDivider
                    />
                  </MenuSectionV2>
                  <div
                    ref={menuScrollRef}
                    id={listboxId}
                    role={"listbox"}
                    data-testid={listboxTestId}
                    // Required whenever more than one option can be selected at once:
                    // without it the multi listbox reports several selected options
                    // while declaring itself single-select, and assistive tech has no
                    // way to know that toggling one does not deselect the rest.
                    aria-multiselectable={isMulti ? true : undefined}
                    className={classNames(
                      "min-h-0 [&>[role=group]:first-child]:pt-0",
                      MENU_SCROLLER_CLASS,
                      menuClassName,
                    )}
                    onScroll={handleMenuScroll}
                  >
                    {menuBody}
                    {loadingMoreRow}
                    <MenuScrollEndFade ref={menuScrollFadeRef} />
                  </div>
                  {menuFooter != null && (
                    <MenuSectionV2 className={"shrink-0"}>{menuFooter}</MenuSectionV2>
                  )}
                </MenuContainerV2>
              ) : menuFooter != null ? (
                <MenuContainerV2
                  className={"flex min-h-0 flex-col"}
                  style={{ maxHeight: menuMaxHeight }}
                >
                  <div
                    ref={menuScrollRef}
                    id={listboxId}
                    role={"listbox"}
                    data-testid={listboxTestId}
                    aria-multiselectable={isMulti ? true : undefined}
                    className={classNames("min-h-0", MENU_SCROLLER_CLASS, menuClassName)}
                    onScroll={handleMenuScroll}
                  >
                    {menuBody}
                    {loadingMoreRow}
                    <MenuScrollEndFade ref={menuScrollFadeRef} />
                  </div>
                  <MenuSectionV2 className={"shrink-0"}>{menuFooter}</MenuSectionV2>
                </MenuContainerV2>
              ) : (
                <MenuContainerV2
                  ref={menuScrollRef}
                  id={listboxId}
                  role={"listbox"}
                  data-testid={listboxTestId}
                  aria-multiselectable={isMulti ? true : undefined}
                  className={classNames(MENU_SCROLLER_CLASS, menuClassName)}
                  style={{ maxHeight: menuMaxHeight }}
                  onScroll={handleMenuScroll}
                >
                  {menuBody}
                  {loadingMoreRow}
                  <MenuScrollEndFade ref={menuScrollFadeRef} />
                </MenuContainerV2>
              )}
            </div>
          </FloatingPortal>
        )}
      </div>

      {error && (
        <p
          id={errorId}
          role={"alert"}
          className={classNames(
            "pt-cds-6 pl-cds-8 !text-cds-error",
            DesignSystemTypography[errorTextVariant[size]],
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
}

const ComboboxChipV2: React.FC<{
  size: DropdownV2Size;
  custom?: boolean;
  disabled?: boolean;
  removeButtonLabel?: string;
  removeButtonRef?: React.Ref<HTMLButtonElement>;
  onRemoveButtonKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onRemove?: () => void;
  /** Hover tooltip. Stays the plain label even when the body is a rendered node. */
  title?: string;
  // A node, not a string: `valueRenderer` puts caller-owned content in chips. The
  // chip's accessible remove label still comes from `removeButtonLabel`.
  children: React.ReactNode;
}> = ({
  size,
  custom = false,
  disabled = false,
  removeButtonLabel,
  removeButtonRef,
  onRemoveButtonKeyDown,
  onRemove,
  title,
  children,
}) => {
  return (
    <div
      title={title}
      className={classNames(
        "inline-flex max-w-full min-w-0 cursor-default items-center rounded-cds-4 border-[1px]",
        "transition-shadow duration-75 focus-within:shadow-cds-focus",
        custom
          ? "bg-cds-surface-border border-cds-surface-border dark:bg-white/[0.24] dark:border-white/[0.08]"
          : "bg-cds-surface-hover border-cds-surface-border dark:bg-white/[0.08] dark:border-white/[0.04]",
        "pl-cds-6 pr-cds-4 gap-cds-4 shrink-0",
        chipHeightClass[size],
        disabled && "opacity-60",
      )}
    >
      <span
        className={classNames(
          "flex min-w-0 flex-1 flex-col justify-center leading-[0]",
          "!text-cds-ink dark:!text-zinc-200",
        )}
      >
        <span
          className={classNames(
            "block truncate leading-[16px]",
            DesignSystemTypography[chipTextVariant[size]],
          )}
        >
          {children}
        </span>
      </span>
      {onRemove && (
        <button
          ref={removeButtonRef}
          type={"button"}
          aria-label={removeButtonLabel}
          disabled={disabled}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={() => {
            if (!disabled) {
              onRemove();
            }
          }}
          onKeyDown={onRemoveButtonKeyDown}
          className={classNames(
            "group inline-flex shrink-0 items-center justify-center rounded-cds-2",
            "focus:outline-none focus-visible:outline-none",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
          )}
        >
          <Icon
            name={"close"}
            size={"sm"}
            className={classNames(
              custom
                ? "!text-cds-ink-subtle group-hover:!text-zinc-600 dark:!text-zinc-400 dark:group-hover:!text-zinc-300"
                : "!text-cds-ink-muted group-hover:!text-zinc-500 dark:!text-zinc-500 dark:group-hover:!text-zinc-400",
            )}
            strokeWidth={2}
          />
        </button>
      )}
    </div>
  );
};
